/* eslint-disable custom-rules/modal-and-drawer-requires-title */
import { useMediaQuery } from '@uhg-abyss/web/hooks/useMediaQuery';
import { Drawer } from '@uhg-abyss/web/ui/Drawer';
import { Flex } from '@uhg-abyss/web/ui/Flex';
import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';
import { Tooltip } from '@uhg-abyss/web/ui/Tooltip';
import find from 'lodash/find';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';

import { PortalContext } from '../../context/PortalContext';
import { getLanguage } from '../../frontends/ProviderSearch/context/Internationalization/helpers';
import { useLagoon } from '../../hooks/useLagoon';
import { SnackCardProvider } from '../../hooks/useSnackCardProviderSearch/useSnackCardProviderSearch';
import { StoreKeys } from '../../hooks/useStore/state';
import { useStore } from '../../hooks/useStore/useStore';
import { CustomPreferredPolicy } from '../../models/Lagoon';
import { FacilityDDPCode, Provider } from '../../models/Provider';
import { ResponseHeaders } from '../../models/ResponseHeaders';
import { useChipStore } from '../../store/useChipStore';
import { ChipState } from '../../store/useChipStore/chipStore';
import { returnSuppressFlag } from '../../utils/featureSuppress';
import { shouldUseGehaV2Styling } from '../../utils/gehaThemeUtils';
import {
  displayPreferredFacilityOnCards,
  getPremiumCarePhysician,
  getSuppressFacilityFlags,
  isShowPreferredFacilityBadge,
} from '../../utils/providerDetails.utils';
import { removeLeadingZeroes } from '../../utils/string.utils';
import {
  getCurrentMember,
  getCustomPreferredPolicyData,
  getIsCustomPreferredPolicy,
  getIsOxfordAndUSP,
  getLoggedInMember,
  getNetworkIdsForPES,
  getSitusState,
} from '../../utils/user.utils';
import { CareCategories, Constants, getPremiumCareContent } from '../Constants';
import { ConstantsLagoon } from '../ConstantsLagoon';
import { phoneOnly } from '../ConstantsStyles';
import {
  displayPremiumCareIdentifier,
  displayTier1Identifier,
  displayTier2Identifier,
  initializeCardVariantsPopupState,
  isPremiumCareDisplayEligible,
} from '../DataCard/DataCardUtils';
import { LinkWithTracking } from '../LinkWithTracking/LinkWithTracking';
import { PortalIdentifier, PortalName } from '../Portal';
import { PSXIcon } from '../PSXIcon';
import { getFeatureFlag } from '../Utils';
import {
  convertProviderTypeToAdobeType,
  formatProviderId,
  handleAdobeSearchResultClickAndModalEvent,
} from '../Utils/adobeTrackUtils/adobeTrackUtils';
import { getTierProviderEligibility } from '../Utils/memberUtils/memberUtils';
import {
  calculateCardLength,
  calculateWebCardLength,
  truncateLargeStrings,
} from '../Utils/truncateLargeStrings';
import {
  GreenDiamond,
  PreferredProviderWebUMR,
} from './PreferredProvider.styles';
import {
  PremiumCareDrawerLinkStyle,
  PremiumCareDrawerStyles,
  PremiumCarePopoverLinkStyle,
  PremiumCareSnackCardPopoverStyles,
  PremiumInfoBoxTextUMR,
  PremiumInfoBoxUMR,
  PremiumPopoverText,
} from './PremiumCare.styles';
import { PremiumCareText, TierCardText } from './SnackCard.styles';
import { TierOne } from './TierOne';
import { TierTwo } from './TierTwo';

export enum CardInfoVariants {
  PREMIUM_CARE = 'premium-care',
  PREFERRED_FACILITY = 'preferred-facility',
  PREFERRED_PROVIDER = 'preferred-provider',
}

export enum CardTypeVariants {
  SNACK_CARD_CONTAINER = 'snack-card-container',
  SNACK_CARD_CAROUSEL = 'snack-card-carousel',
  SNACK_CARD_INTERNAL = 'snack-card-internal',
  DATA_CARD = 'data-card',
  SNACK_CARD_MAP_POPUP = 'snack-card-map-popup',
  SNACK_CARD_MAP_POPUP_INTERNAL = 'snack-card-map-popup-internal',
}

type Props = {
  cardType: string;
  headers?: ResponseHeaders;
  indexForAnalytics?: number;
  rulesPackageKey?: string;
  practitioner?: Provider | SnackCardProvider;
  locationForAnalytics?: string;
  selectedFilters?: string;
  searchTerm?: string;
  landingPage?: boolean;
  popupContainer?: boolean;
  isSmartChoiceFFEnabled?: boolean;
  onClosePopup?: () => void;
  displayTop?: boolean;
  displayMiddle?: boolean;
};

export const CardInfoForUMR = ({
  cardType,
  selectedFilters,
  indexForAnalytics,
  locationForAnalytics,
  headers = { correlationId: '' },
  practitioner = {},
  searchTerm,
  landingPage = false,
  popupContainer = false,
  isSmartChoiceFFEnabled = false,
  onClosePopup,
  displayTop = false,
  displayMiddle = false,
}: Props) => {
  const { t } = useTranslation();
  const mobileScreen = useMediaQuery(phoneOnly);
  const { portalData } = useContext(PortalContext);
  const featureFlags = useLagoon('feature-flags')();
  const isGeha = portalData?.portalIdentifier === PortalIdentifier.GEHA;
  const isGEHAV2 = shouldUseGehaV2Styling();
  const isGehaTheme = isGEHAV2 || isGeha;

  const { coverageType, storeLob } = useChipStore(
    useShallow((state: ChipState) => ({
      coverageType: state.coverageType,
      storeLob: state.lob,
    }))
  );
  const chipStore = useChipStore(useShallow((state: ChipState) => state));
  const { chipValue } = chipStore;

  const [isMobileDrawerOpen, setIsMobileDrawerOpen] = useState<
    Record<CardInfoVariants, boolean>
  >(
    initializeCardVariantsPopupState(CardInfoVariants) as Record<
      CardInfoVariants,
      boolean
    >
  );
  const [isOpen, setIsOpen] = useState<Record<CardInfoVariants, boolean>>(
    initializeCardVariantsPopupState(CardInfoVariants) as Record<
      CardInfoVariants,
      boolean
    >
  );

  const uiMessaging = useLagoon('ui-messaging')();
  const customPreferredPoliciesData: CustomPreferredPolicy[] = useLagoon(
    'custom-preferred-policies'
  )();

  const customPreferredPolicies = customPreferredPoliciesData?.map(
    (policy) => ({ ...policy, policyId: removeLeadingZeroes(policy.policyId) })
  );

  const customPreferredPoliciesWithLabel = customPreferredPoliciesData?.filter(
    (policy) => policy?.badgeLabel && policy?.icon
  );

  const memberHasRPK = useStore(StoreKeys.TIER1_PLAN_FLAG);
  const ddpCodeUser = getLoggedInMember()?.ddpCode;
  const ddpCode = practitioner?.ddpCode as FacilityDDPCode;

  const {
    providerType,
    isTieredProvider,
    isTier2Provider,
    isPremiumCarePhysician,
    isPreferredLab = false,
    isFreeStandingFacility = false,
    isOxPreferredLab,
  } = practitioner;

  const enablePremiumProviderBadgeNewDisplay: boolean = getFeatureFlag(
    featureFlags,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_PREMIUM_PROVIDER_BADGE_NEW_DISPLAY
  );

  const preferredFacilityFlag: boolean = getFeatureFlag(
    featureFlags,
    ConstantsLagoon.FEATURE_FLAGS.PREFERRED_FACILITY_IDENTIFICATION
  );
  const currentMember = getCurrentMember();
  const isTier1ProviderEligible = displayTier1Identifier(isTieredProvider);

  const isDisplayTier1Identifier = getTierProviderEligibility(
    currentMember,
    isTier1ProviderEligible
  );

  const suppressPremiumCare = returnSuppressFlag(
    ConstantsLagoon.FEATURE_SUPPRESSION_FLAGS.PREMIUM_CARE
  );
  const reciprocityId = getNetworkIdsForPES(
    currentMember,
    coverageType,
    featureFlags
  );

  const isDisplayTier2Identifier = displayTier2Identifier(
    isTier2Provider,
    currentMember?.isIFPTieredPlan
  );

  const situsState = getSitusState(currentMember, storeLob);

  const isCustomPreferredPolicy: boolean = getIsCustomPreferredPolicy(
    customPreferredPoliciesWithLabel,
    currentMember?.policyNumber,
    reciprocityId,
    situsState
  );
  const isMultipleBadgeCardViewEnabled: boolean = getFeatureFlag(
    featureFlags,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_MULTIPLE_BADGE_DATA_CARD_VIEW
  );
  const isInternalCardType =
    cardType === CardTypeVariants.SNACK_CARD_INTERNAL ||
    cardType === CardTypeVariants.SNACK_CARD_MAP_POPUP_INTERNAL;
  const showMultipleBadgeOnDataCard: boolean =
    isMultipleBadgeCardViewEnabled && cardType === CardTypeVariants.DATA_CARD;
  const showMultipleBadgeOnSnackCard: boolean =
    isMultipleBadgeCardViewEnabled && isInternalCardType;

  let showPremiumCare = displayPremiumCareIdentifier(
    !suppressPremiumCare,
    getPremiumCarePhysician(isPremiumCarePhysician),
    isDisplayTier1Identifier,
    isDisplayTier2Identifier,
    memberHasRPK,
    isCustomPreferredPolicy,
    isMultipleBadgeCardViewEnabled
  );

  if (isMultipleBadgeCardViewEnabled) {
    const isEligibleCardType =
      cardType === CardTypeVariants.DATA_CARD ||
      isInternalCardType ||
      cardType === CardTypeVariants.SNACK_CARD_MAP_POPUP;

    showPremiumCare = isEligibleCardType
      ? isPremiumCareDisplayEligible(
          !suppressPremiumCare,
          getPremiumCarePhysician(isPremiumCarePhysician)
        )
      : false;
  }

  const { isOXMember } = getIsOxfordAndUSP(
    currentMember,
    coverageType,
    featureFlags
  );

  const oxfordPlnFlag: boolean = getFeatureFlag(
    featureFlags,
    ConstantsLagoon.FEATURE_FLAGS.OXFORD_PLN
  );

  const suppressFacilityFlags = getSuppressFacilityFlags();
  const showBenefitsBadge = isShowPreferredFacilityBadge(
    suppressFacilityFlags,
    preferredFacilityFlag,
    ddpCode,
    ddpCodeUser,
    isPreferredLab,
    isOxPreferredLab,
    isOXMember,
    isFreeStandingFacility,
    oxfordPlnFlag
  );

  const showGreenDiamondTag = getFeatureFlag(
    featureFlags,
    ConstantsLagoon.FEATURE_FLAGS.GREEN_DIAMOND_IDENTIFICATION
  );

  const isGreenDiamondIndicator = practitioner?.isGreenDiamondProvider;
  const isValidGreenDiamondProvider =
    isGreenDiamondIndicator && showGreenDiamondTag;
  const variants: CardInfoVariants[] = [];

  const isDisplayPremiumCareMapPopup =
    isMultipleBadgeCardViewEnabled &&
    cardType === CardTypeVariants.SNACK_CARD_MAP_POPUP
      ? !isValidGreenDiamondProvider &&
        !isDisplayTier1Identifier &&
        !isDisplayTier2Identifier
      : true;

  const isDisplayPremiumCareMapPopupInternal =
    !isMultipleBadgeCardViewEnabled &&
    cardType === CardTypeVariants.SNACK_CARD_MAP_POPUP_INTERNAL
      ? isValidGreenDiamondProvider ||
        isDisplayTier1Identifier ||
        isDisplayTier2Identifier
      : true;

  const addCardInfoVariant = (
    condition: boolean,
    variantType: CardInfoVariants
  ) => {
    if (condition && !variants.includes(variantType))
      variants.push(variantType);
  };

  if (showMultipleBadgeOnSnackCard) {
    addCardInfoVariant(
      isDisplayPremiumCareMapPopupInternal && showPremiumCare,
      CardInfoVariants.PREMIUM_CARE
    );
    addCardInfoVariant(
      !!isValidGreenDiamondProvider,
      CardInfoVariants.PREFERRED_PROVIDER
    );
  } else {
    addCardInfoVariant(
      isDisplayPremiumCareMapPopup &&
        (showMultipleBadgeOnDataCard
          ? showPremiumCare
          : !isGreenDiamondIndicator && showPremiumCare),
      CardInfoVariants.PREMIUM_CARE
    );
    addCardInfoVariant(
      !isGreenDiamondIndicator &&
        !!displayPreferredFacilityOnCards(
          showPremiumCare,
          showBenefitsBadge,
          isDisplayTier1Identifier,
          isDisplayTier2Identifier
        ),
      CardInfoVariants.PREFERRED_FACILITY
    );
  }

  const locale = getLanguage().code;
  const premiumCareData = getPremiumCareContent(locale);
  const linkUrl = premiumCareData?.popoverURL ?? '';
  const premiumContent = premiumCareData?.popoverContent;

  const preferredFacilityContent = find(uiMessaging, {
    key: ConstantsLagoon.CONTENT_FILTERS.PREFERRED_FACILITY_CONTENT,
  })?.message;

  const customPreferredContent = getCustomPreferredPolicyData(
    customPreferredPolicies,
    currentMember?.policyNumber,
    reciprocityId,
    situsState
  );

  const preferredProviderContent =
    customPreferredContent?.popoverContent ??
    Constants.PREFERRED_PROVIDER_BENEFITS.CONTENT;
  const preferredProviderHeading =
    customPreferredContent?.badgeLabel ??
    Constants.PREFERRED_PROVIDER_BENEFITS.HEADING;
  const preferredProviderIcon =
    customPreferredContent?.icon ??
    Constants.PREFERRED_PROVIDER_BENEFITS.GREEN_DIAMOND;

  const mapSnackcard =
    cardType === CardTypeVariants.SNACK_CARD_MAP_POPUP || false;

  useEffect(() => {
    Object.keys(isMobileDrawerOpen).forEach((key) => {
      if (isMobileDrawerOpen[key]) {
        handleAdobeSearchResultClickAndModalEvent({
          filters: selectedFilters,
          linkName: `provider-${key}-drawer`,
          linkPosition: indexForAnalytics,
          linkLocation: locationForAnalytics,
          modalName:
            key === CardInfoVariants.PREMIUM_CARE
              ? Constants.PREMIUM_CARE_PAGE_LOCATION
              : Constants.PREFERRED_FACILITY_PAGE_LOCATION,
          customAttributesBlock: {
            correlationId: headers?.correlationId,
            providerId: formatProviderId(practitioner),
            providerType: convertProviderTypeToAdobeType(providerType),
          },
          term: searchTerm ?? '',
        });
      }
    });
  }, [isMobileDrawerOpen]);

  useEffect(() => {
    Object.keys(isOpen).forEach((key) => {
      if (isOpen[key]) {
        handleAdobeSearchResultClickAndModalEvent({
          filters: selectedFilters,
          linkName: `provider-${key}-popover`,
          linkPosition: indexForAnalytics,
          linkLocation: locationForAnalytics,
          modalName:
            key === CardInfoVariants.PREMIUM_CARE
              ? Constants.PREMIUM_CARE_PAGE_LOCATION
              : Constants.PREFERRED_FACILITY_PAGE_LOCATION,
          customAttributesBlock: {
            correlationId: headers?.correlationId,
            providerId: formatProviderId(practitioner),
            providerType: convertProviderTypeToAdobeType(providerType),
          },
          term: searchTerm ?? '',
        });
      }
    });
  }, [isOpen]);

  const ICON_MAP = {
    [CardInfoVariants.PREMIUM_CARE]: (
      <>
        <IconSymbol
          color="$uhcPrimary1"
          icon="favorite"
          size={mapSnackcard ? 12 : 16}
        />
        <IconSymbol
          color="$uhcPrimary1"
          icon="favorite"
          size={mapSnackcard ? 12 : 16}
        />
      </>
    ),
    [CardInfoVariants.PREFERRED_FACILITY]: null,
    [CardInfoVariants.PREFERRED_PROVIDER]: null,
  };

  const LINK_MAP = {
    [CardInfoVariants.PREMIUM_CARE]: (
      <LinkWithTracking
        analyticsInfo={{
          name: `${CardInfoVariants.PREMIUM_CARE}-benefits-link`,
          location: `modal:${Constants.PREMIUM_CARE_PHYSICIANS.TAG.toLowerCase()}`,
          type: 'external',
          destinationUrl: linkUrl,
        }}
        css={PremiumCarePopoverLinkStyle}
        data-auto-testid={`${CardInfoVariants.PREMIUM_CARE}-benefits-link`}
        data-testid={`${CardInfoVariants.PREMIUM_CARE}-benefits-link`}
        href={linkUrl}
      >
        {t('PREMIUM_CARE_BENEFITS.PREMIUM_CARE_LINK')}
      </LinkWithTracking>
    ),
    [CardInfoVariants.PREFERRED_FACILITY]: null,
    [CardInfoVariants.PREFERRED_PROVIDER]: null,
  };

  const TAG_MAP = {
    [CardInfoVariants.PREMIUM_CARE]: showMultipleBadgeOnDataCard
      ? t('PREMIUM_CARE_BENEFITS.PREMIUM_CARE_PHYSICIAN_TAG')
      : t('PREMIUM_CARE_BENEFITS.PREMIUM_CARE_TAG'),
    [CardInfoVariants.PREFERRED_FACILITY]: t('PREFERRED_FACILITY_BENEFITS.TAG'),
    [CardInfoVariants.PREFERRED_PROVIDER]: t(preferredProviderHeading),
  };

  const POPOVER_CONTENT_MAP = {
    [CardInfoVariants.PREMIUM_CARE]:
      premiumContent ?? t('PREMIUM_CARE_BENEFITS.PREMIUM_CARE_CONTENT'),
    [CardInfoVariants.PREFERRED_FACILITY]:
      preferredFacilityContent ?? t('PREFERRED_FACILITY_BENEFITS.CONTENT'),
    [CardInfoVariants.PREFERRED_PROVIDER]:
      preferredProviderContent ?? t('PREFERRED_PROVIDER_BENEFITS.CONTENT'),
  };

  const POPOVER_HEADING_MAP = {
    [CardInfoVariants.PREMIUM_CARE]: t(
      'PREMIUM_CARE_BENEFITS.PREMIUM_CARE_HEADING'
    ),
    [CardInfoVariants.PREFERRED_FACILITY]: t(
      'PREFERRED_FACILITY_BENEFITS.HEADING'
    ),
    [CardInfoVariants.PREFERRED_PROVIDER]: t(preferredProviderHeading),
  };

  const POPOVER_WIDTH_MAP = {
    [CardInfoVariants.PREMIUM_CARE]: 363,
    [CardInfoVariants.PREFERRED_FACILITY]: 220,
  };

  const premiumInfoBoxBadge = (variant, cardType) => {
    const badgeContent = TAG_MAP?.[variant];
    const sumOfAllBadgeText = variants.reduce(
      (previousValue, currentValue) =>
        TAG_MAP?.[currentValue]?.length + previousValue,
      0
    );
    const snackCardCss = mapSnackcard
      ? {
          fontSize: '12px !important',
          fontWeight: '600',
          lineHeight: '16px ',
        }
      : {};
    const truncateLength = calculateCardLength(
      popupContainer,
      cardType,
      variants,
      sumOfAllBadgeText,
      CardTypeVariants
    );

    return enablePremiumProviderBadgeNewDisplay && isInternalCardType ? (
      <PremiumInfoBoxTextUMR>
        <PremiumCareText css={{ marginLeft: '0px' }}>
          {truncateLargeStrings(badgeContent, truncateLength)}
        </PremiumCareText>
      </PremiumInfoBoxTextUMR>
    ) : (
      <TierCardText css={snackCardCss}>
        {truncateLargeStrings(badgeContent, truncateLength)}
      </TierCardText>
    );
  };

  const webPreferredIndicatorOrTier = (variant, cardType) => {
    let truncateLength = calculateWebCardLength(
      popupContainer,
      cardType,
      CardTypeVariants
    );
    const textTruncated = TAG_MAP?.[variant]?.length >= truncateLength;
    isUmr && mobileScreen ? (truncateLength = 18) : truncateLength;

    return (
      variant === CardInfoVariants.PREFERRED_PROVIDER && (
        <PreferredProviderWebUMR
          css={{
            backgroundColor: isSmartChoiceFFEnabled
              ? 'transparent'
              : '$core.color.green.20',
          }}
          data-auto-testid={`${variant}-popover`}
          data-testid={`${variant}-popover`}
          onClick={() =>
            mobileScreen
              ? setIsMobileDrawerOpen({
                  ...isMobileDrawerOpen,
                  [variant]: true,
                })
              : setIsOpen({ ...isOpen, [variant]: true })
          }
        >
          <GreenDiamond
            css={{ marginLeft: isSmartChoiceFFEnabled ? 0 : '4px' }}
          >
            <PSXIcon icon={preferredProviderIcon} size={16} />
          </GreenDiamond>
          {textTruncated ? (
            <div>
              <Tooltip content={TAG_MAP?.[variant]} position="top">
                <TierCardText
                  style={{
                    marginTop: '5px',
                    marginRight: '4px',
                    fontSize: '12px',
                  }}
                >
                  {truncateLargeStrings(TAG_MAP?.[variant], truncateLength)}
                </TierCardText>
              </Tooltip>
            </div>
          ) : (
            <TierCardText style={{ fontSize: '12px' }}>
              {truncateLargeStrings(TAG_MAP?.[variant], truncateLength)}
            </TierCardText>
          )}
        </PreferredProviderWebUMR>
      )
    );
  };

  if (
    isValidGreenDiamondProvider &&
    !preferredProviderIcon &&
    !preferredProviderHeading
  ) {
    return null;
  }
  const BADGE_ROW_MIN_HEIGHT = '24px';
  const hasPreferredProviderVariant = variants.includes(
    CardInfoVariants.PREFERRED_PROVIDER
  );
  const hasPremiumCareVariant = variants.includes(
    CardInfoVariants.PREMIUM_CARE
  );
  const hasTier1Top = isDisplayTier1Identifier;
  const hasTier2Top =
    !isGreenDiamondIndicator &&
    !isDisplayTier1Identifier &&
    isDisplayTier2Identifier;

  const hasTopContent =
    hasTier1Top || hasTier2Top || hasPreferredProviderVariant;
  const hasMiddleContent = hasPremiumCareVariant;
  const isUmr = portalData?.portalName === PortalName.UMR;

  function displayBadgesOnTop() {
    if (
      !hasTopContent &&
      cardType !== CardTypeVariants.SNACK_CARD_MAP_POPUP_INTERNAL &&
      (chipValue === CareCategories.PRIMARY_CARE ||
        chipValue === CareCategories.MEDICAL_SPECIALISTS)
    ) {
      return (
        <Flex
          aria-hidden="true"
          data-testid="umr-top-placeholder"
          style={{
            minHeight: BADGE_ROW_MIN_HEIGHT,
            visibility: 'hidden',
          }}
        />
      );
    }
    return (
      <Flex style={{ flexWrap: 'nowrap' }}>
        {isDisplayTier1Identifier && (
          <TierOne
            cardType={cardType}
            data-auto-testid={`tier-one-${cardType}-tag`}
            data-testid={`tier-one-${cardType}-tag`}
            isSmartChoiceFFEnabled={isSmartChoiceFFEnabled}
            isTierOne={isDisplayTier1Identifier}
          />
        )}
        {!isGreenDiamondIndicator &&
          !isDisplayTier1Identifier &&
          isDisplayTier2Identifier && (
            <TierTwo
              cardType={cardType}
              data-auto-testid={`tier-two-${cardType}-tag`}
              data-testid={`tier-two-${cardType}-tag`}
              isSmartChoiceFFEnabled={isSmartChoiceFFEnabled}
              isTierTwo={isDisplayTier2Identifier}
            />
          )}
        {variants &&
          variants.map((variant, index) => (
            <div
              data-auto-testid={`${variant}-${cardType}-tag`}
              data-testid={`${variant}-${cardType}-tag`}
              key={index}
            >
              {variant === CardInfoVariants.PREFERRED_PROVIDER && (
                <>
                  <Drawer
                    css={PremiumCareDrawerStyles}
                    data-auto-testid={`provider-${variant}-drawer`}
                    data-testid={`provider-${variant}-drawer`}
                    header={POPOVER_HEADING_MAP?.[variant]}
                    isOpen={isMobileDrawerOpen[variant]}
                    onClose={() => {
                      setIsMobileDrawerOpen({
                        ...isMobileDrawerOpen,
                        [variant]: false,
                      });
                      if (onClosePopup) onClosePopup();
                    }}
                    position="right"
                    size="200"
                  >
                    {POPOVER_CONTENT_MAP?.[variant]}
                    {variant === CardInfoVariants.PREFERRED_PROVIDER ? (
                      <LinkWithTracking
                        analyticsInfo={{
                          name: `${String(variant)}-benefits-link`,
                          location: `modal:${Constants.PREMIUM_CARE_PHYSICIANS.TAG.toLowerCase()}`,
                          type: 'external',
                          destinationUrl: linkUrl,
                        }}
                        css={PremiumCareDrawerLinkStyle}
                        data-auto-testid={`${String(variant)}-benefits-link`}
                        data-testid={`${String(variant)}-benefits-link`}
                        href={linkUrl}
                      >
                        {t('PREMIUM_CARE_BENEFITS.PREMIUM_CARE_LINK')}
                      </LinkWithTracking>
                    ) : null}
                  </Drawer>
                  <PremiumCareSnackCardPopoverStyles
                    content={
                      <div>
                        <PremiumPopoverText>
                          {POPOVER_CONTENT_MAP?.[variant]}
                        </PremiumPopoverText>
                        {LINK_MAP[variant]}
                      </div>
                    }
                    cssProps={{ landingPage }}
                    data-auto-testid={`provider-${variant}-popover`}
                    data-testid={`provider-${variant}-popover`}
                    onOpenChange={() =>
                      setIsOpen({ ...isOpen, [variant]: !isOpen[variant] })
                    }
                    open={isOpen[variant]}
                    title={POPOVER_HEADING_MAP?.[variant]}
                    width={POPOVER_WIDTH_MAP?.[variant]}
                  >
                    {webPreferredIndicatorOrTier(variant, cardType)}
                  </PremiumCareSnackCardPopoverStyles>
                </>
              )}
            </div>
          ))}
      </Flex>
    );
  }
  if (isUmr && displayTop) {
    return displayBadgesOnTop();
  }

  function displayBadgesOnMiddle() {
    if (
      !hasMiddleContent &&
      cardType !== CardTypeVariants.SNACK_CARD_MAP_POPUP_INTERNAL &&
      (chipValue === CareCategories.PRIMARY_CARE ||
        chipValue === CareCategories.MEDICAL_SPECIALISTS)
    ) {
      return (
        <Flex
          aria-hidden="true"
          data-testid="umr-middle-placeholder"
          style={{
            minHeight: BADGE_ROW_MIN_HEIGHT,
            visibility: 'hidden',
          }}
        />
      );
    }
    return (
      <Flex style={{ flexWrap: 'nowrap' }}>
        {variants &&
          variants.map((variant, index) => (
            <div
              data-auto-testid={`${variant}-${cardType}-tag`}
              data-testid={`${variant}-${cardType}-tag`}
              key={index}
            >
              <Drawer
                css={PremiumCareDrawerStyles}
                data-auto-testid={`provider-${variant}-drawer`}
                data-testid={`provider-${variant}-drawer`}
                header={POPOVER_HEADING_MAP?.[variant]}
                isOpen={isMobileDrawerOpen[variant]}
                onClose={() => {
                  setIsMobileDrawerOpen({
                    ...isMobileDrawerOpen,
                    [variant]: false,
                  });
                  if (onClosePopup) onClosePopup();
                }}
                position="right"
                size="200"
              >
                {POPOVER_CONTENT_MAP?.[variant]}
                {variant === CardInfoVariants.PREMIUM_CARE ? (
                  <LinkWithTracking
                    analyticsInfo={{
                      name: `${variant}-benefits-link`,
                      location: `modal:${Constants.PREMIUM_CARE_PHYSICIANS.TAG.toLowerCase()}`,
                      type: 'external',
                      destinationUrl: linkUrl,
                    }}
                    css={PremiumCareDrawerLinkStyle}
                    data-auto-testid={`${variant}-benefits-link`}
                    data-testid={`${variant}-benefits-link`}
                    href={linkUrl}
                  >
                    {t('PREMIUM_CARE_BENEFITS.PREMIUM_CARE_LINK')}
                  </LinkWithTracking>
                ) : null}
              </Drawer>

              <>
                {variant === CardInfoVariants.PREMIUM_CARE && (
                  <PremiumCareSnackCardPopoverStyles
                    content={
                      <div>
                        <PremiumPopoverText>
                          {POPOVER_CONTENT_MAP?.[variant]}
                        </PremiumPopoverText>
                        {LINK_MAP[variant]}
                      </div>
                    }
                    cssProps={{ landingPage }}
                    data-auto-testid={`provider-${variant}-popover`}
                    data-testid={`provider-${variant}-popover`}
                    onOpenChange={() =>
                      setIsOpen({ ...isOpen, [variant]: !isOpen[variant] })
                    }
                    open={isOpen[variant]}
                    title={POPOVER_HEADING_MAP?.[variant]}
                    width={POPOVER_WIDTH_MAP?.[variant]}
                  >
                    <PremiumInfoBoxUMR
                      css={{
                        backgroundColor:
                          (enablePremiumProviderBadgeNewDisplay &&
                            isInternalCardType) ||
                          isSmartChoiceFFEnabled
                            ? 'transparent'
                            : isGehaTheme
                            ? '$core.color.neutral.10'
                            : '',
                        marginBottom: !showMultipleBadgeOnSnackCard
                          ? '4px'
                          : '',
                      }}
                      cssProps={{ landingPage }}
                      data-auto-testid={`${variant}-popover`}
                      data-testid={`${variant}-popover`}
                      isMultiple={
                        isMultipleBadgeCardViewEnabled &&
                        (isDisplayTier1Identifier ||
                          isDisplayTier2Identifier ||
                          isValidGreenDiamondProvider)
                      }
                      onClick={() =>
                        mobileScreen
                          ? setIsMobileDrawerOpen({
                              ...isMobileDrawerOpen,
                              [variant]: true,
                            })
                          : setIsOpen({ ...isOpen, [variant]: true })
                      }
                    >
                      {ICON_MAP[variant]}
                      {premiumInfoBoxBadge(variant, cardType)}
                    </PremiumInfoBoxUMR>
                  </PremiumCareSnackCardPopoverStyles>
                )}
              </>
            </div>
          ))}
      </Flex>
    );
  }

  if (isUmr && displayMiddle) {
    return displayBadgesOnMiddle();
  }

  return null;
};
